
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/auth/useAuth";
import { useAuthRedirect } from "@/hooks/auth/useAuthRedirect";
import BrandedLoader from "@/components/ui/BrandedLoader";

interface AuthGuardProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

export const AuthGuard = ({ children, adminOnly = false }: AuthGuardProps) => {
  const location = useLocation();
  const { toast } = useToast();
  const { 
    user,
    isLoading,
    isInitialized,
    isAuthenticated,
    isAdmin,
    updateLastActivity
  } = useAuth();
  const { redirectToLogin, restoreSessionFromUrl, isReady } = useAuthRedirect();
  
  // Use a single state for loader
  const [showLoader, setShowLoader] = useState(true);
  const [restoringSession, setRestoringSession] = useState(false);
  
  // Try to restore session from URL params if present
  useEffect(() => {
    // Check if we have a token in the URL
    const params = new URLSearchParams(location.search);
    if (params.has('token')) {
      setRestoringSession(true);
      restoreSessionFromUrl().finally(() => {
        setRestoringSession(false);
      });
    }
  }, [location.search, restoreSessionFromUrl]);
  
  // Handle redirects when auth state changes
  useEffect(() => {
    // Only process redirects when auth is initialized and not restoring session
    if (isReady && isInitialized && !restoringSession) {
      // If not authenticated, redirect to login
      if (!isAuthenticated) {
        console.log("AuthGuard - Not authenticated, redirecting to login");
        redirectToLogin(location.pathname);
      }
      
      // If admin route but user is not admin, redirect
      if (adminOnly && !isAdmin && isAuthenticated) {
        toast({
          title: "Access denied",
          description: "You need admin privileges to access this page.",
          variant: "destructive",
        });
        redirectToLogin(location.pathname);
      }
    }
  }, [
    isReady,
    isInitialized,
    isAuthenticated,
    adminOnly, 
    isAdmin, 
    location.pathname, 
    redirectToLogin, 
    toast,
    restoringSession
  ]);
  
  // Update loading state
  useEffect(() => {
    // Check if we should hide the loader
    if (isInitialized && !restoringSession) {
      // Small delay to prevent flashing
      const timer = setTimeout(() => {
        setShowLoader(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    
    // Fallback timeout to ensure loader doesn't get stuck
    const timeoutTimer = setTimeout(() => {
      console.log("Auth loader timeout reached");
      setShowLoader(false);
    }, 3000);
    
    return () => clearTimeout(timeoutTimer);
  }, [isInitialized, restoringSession]);
  
  // Update last activity
  useEffect(() => {
    if (isAuthenticated) {
      updateLastActivity();
    }
  }, [isAuthenticated, updateLastActivity, location.pathname]);
  
  // Show loader during initial auth check
  if (showLoader) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <BrandedLoader />
        {restoringSession && (
          <p className="mt-4 text-center text-muted-foreground">Restoring your session...</p>
        )}
      </div>
    );
  }
  
  // For admin routes, check admin status
  if (adminOnly && !isAdmin) {
    return null;
  }
  
  // Only render children if authenticated or we hit the timeout
  return isAuthenticated ? <>{children}</> : null;
};
